/**
 * Shared client search: one typed result shape for the Navbar overlay and the
 * /search page, one in-memory cache per query. The overlay has already fetched
 * the query the user is typing, so submitting it must not fetch again. The
 * endpoint has no size parameter and returns every match (200+ rows, ~800 KB
 * for a common word), which is why results are also capped client-side.
 */
export type SearchItem = {
  _id: string
  title: string
  description?: string
  thumbnail?: string
  thumbnailResize?: string
  category?: string
  author?: any
  game?: any
  publishDt?: string
  wordCount?: number
  view?: number
}

/** Most results kept per query (the /search page renders this many). */
export const SEARCH_RESULT_CAP = 30
/** How long a query's results are reused without refetching. */
const SEARCH_CACHE_TTL = 5 * 60_000

const results = new Map<string, { at: number; items: SearchItem[] }>()
const inflight = new Map<string, Promise<SearchItem[]>>()

/** Resolves with the capped result list; rejects on network or `success:false`. */
export const searchContents = (query: string): Promise<SearchItem[]> => {
  const q = query.trim()
  if (!q) return Promise.resolve([])
  const key = q.toLowerCase()

  const hit = results.get(key)
  if (hit && Date.now() - hit.at < SEARCH_CACHE_TTL) return Promise.resolve(hit.items)
  const pending = inflight.get(key)
  if (pending) return pending

  const request = fetch(
    `${process.env.BACKEND_BASE_URL}/contents/search?title=${encodeURIComponent(q)}`,
  )
    .then((res) => res.json())
    .then((data) => {
      if (!data?.success) throw new Error('search failed')
      const items: SearchItem[] = Array.isArray(data.data)
        ? data.data.slice(0, SEARCH_RESULT_CAP)
        : []
      results.set(key, { at: Date.now(), items })
      return items
    })
    .finally(() => {
      inflight.delete(key)
    })
  inflight.set(key, request)
  return request
}
